import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Paper, Typography, IconButton, LinearProgress, CircularProgress, Button } from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import { useDocumentProgress } from '../context/DocumentProgressContext';

// Where a completed row navigates. When the row knows which invoice it produced,
// the link carries a deep-link param that InvoicingPage consumes on arrival: an
// invoice opens the document preview, an email draft opens the draft dialog on
// its history tab. Rows with no `invoiceId` just land on the tab — which now
// means only a run that resolved no invoice at all. A Create Document batch that
// produces a regular + reimbursement pair for one client emits a row per invoice
// (see runCreateDocumentBatches), so each of them deep-links to its own document
// instead of the pair sharing one linkless row.
const itemLink = (item) => {
  // A quotation lives on its own page, so it carries its own id and its own
  // param: QuotationPage consumes `preview=` on arrival and opens that
  // quotation's document. A row that resolved no quotation just lands on the tab.
  if (item.type === 'quotation') {
    const quotBase = `/${item.teamspaceId}/quotation?tab=quotations`;
    return item.quotationId ? `${quotBase}&preview=${item.quotationId}` : quotBase;
  }
  // A TH tax invoice / receipt is its own record on its own tab, so it carries
  // its own param: the `preview=` link below would open the *invoice's* document,
  // which is the wrong one here. A row that resolved no TaxInvoice at all (an
  // error, or a run that created nothing) just lands on the tab.
  if (item.type === 'tax_document') {
    const taxBase = `/${item.teamspaceId}/invoicing?tab=tax-invoice`;
    return item.taxInvoiceId ? `${taxBase}&taxPreview=${item.taxInvoiceId}` : taxBase;
  }
  // A payment reminder has no preview deep link yet — land on its tab, where
  // the new row (and a failed one's error message) is listed.
  if (item.type === 'payment_reminder') {
    return `/${item.teamspaceId}/invoicing?tab=payment-reminders`;
  }
  const base = `/${item.teamspaceId}/invoicing?tab=invoices`;
  if (!item.invoiceId) return base;
  return item.type === 'email_draft'
    ? `${base}&draft=${item.invoiceId}`
    : `${base}&preview=${item.invoiceId}`;
};

const TYPE_LABEL = {
  quotation: 'Quotation',
  email_draft: 'Email draft',
  invoice: 'Invoice',
  tax_document: 'Tax document',
  payment_reminder: 'Payment reminder',
  reconcile: 'Reconcile',
};

// The panel is shared by document generation and email drafting, which can run at
// the same time. Only claim "documents"/"email drafts" when every item agrees;
// otherwise fall back to the neutral noun.
const noun = (items, plural) => {
  const types = new Set(items.map(i => i.type));
  if (types.size === 1 && types.has('email_draft')) return plural ? 'email drafts' : 'email draft';
  if (types.size === 1 && types.has('payment_reminder')) return plural ? 'payment reminders' : 'payment reminder';
  return plural ? 'documents' : 'document';
};

const DocumentProgressPanel = () => {
  const { items, minimized, setMinimized, clearItems, retryItem } = useDocumentProgress();

  if (items.length === 0) return null;

  const doneCount = items.filter(p => p.status === 'done' || p.status === 'error').length;
  const total = items.length;
  const allDone = doneCount === total;
  const successCount = items.filter(p => p.status === 'done').length;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        // Phones: span the screen with 16px gutters, sitting just above the
        // floating add button's default spot (16px in, 56px tall).
        bottom: { xs: 88, sm: 96 },
        left: { xs: 16, sm: 'auto' },
        right: { xs: 16, sm: 24 },
        width: { xs: 'auto', sm: 310 },
        zIndex: 999,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2, py: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          bgcolor: 'editorial.sidebarBg',
          cursor: 'default',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'common.white' }}>
          {allDone
            ? `${successCount} ${noun(items, successCount !== 1)} created${doneCount - successCount > 0 ? `, ${doneCount - successCount} failed` : ''}`
            : `Creating ${noun(items, true)} (${doneCount}/${total})`}
        </Typography>
        <Box sx={{ display: 'flex', ml: 1 }}>
          <IconButton size="small" onClick={() => setMinimized(m => !m)} sx={{ color: 'rgba(255,255,255,0.7)', p: { xs: 1, sm: '5px' } }}>
            {minimized ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
          </IconButton>
          <IconButton size="small" onClick={clearItems} sx={{ color: 'rgba(255,255,255,0.7)', p: { xs: 1, sm: '5px' } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Progress bar */}
      {!allDone && (
        <LinearProgress
          variant="determinate"
          value={(doneCount / total) * 100}
          sx={{ height: 2 }}
          color="secondary"
        />
      )}

      {/* Item list */}
      {!minimized && (
        <Box sx={{ maxHeight: { xs: '40vh', sm: 280 }, overflowY: 'auto' }}>
          {items.map((item, idx) => {
            const isDone = item.status === 'done';
            const content = (
              <Box
                sx={{
                  px: 2, py: 0.9,
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  borderBottom: idx < items.length - 1 ? 1 : 0,
                  borderColor: 'divider',
                  ...(isDone && { cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }),
                }}
              >
                {/* Status icon */}
                <Box sx={{ width: 18, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {item.status === 'generating' && <CircularProgress size={14} color="secondary" />}
                  {item.status === 'done'       && <CheckCircleOutlinedIcon sx={{ fontSize: 16, color: '#66bb6a' }} />}
                  {item.status === 'error'      && <ErrorOutlineIcon        sx={{ fontSize: 16, color: '#ef5350' }} />}
                  {item.status === 'pending'    && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'grey.600' }} />}
                </Box>

                {/* Labels */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ lineHeight: 1.3, fontSize: '0.82rem' }}>
                    {item.statementId || '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {/* The client name matters most before a statement id exists —
                        Create Document adds its rows before the invoice is created,
                        so the top line may still be the client's name at that point. */}
                    {[TYPE_LABEL[item.type] || 'Invoice', item.clientName].filter(Boolean).join(' · ')}
                  </Typography>
                  {/* Set while a request whose answer never arrived (a proxy
                      timeout) is being checked against the server — see
                      utils/documentLanding. Cleared once the row settles. */}
                  {item.status !== 'error' && item.status !== 'done' && item.note && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'normal', lineHeight: 1.3 }}>
                      {item.note}
                    </Typography>
                  )}
                  {/* The document was created, but a secondary step was not — today
                      that is the DCC / PH Google Drive copy. Not an error (the row
                      keeps its green tick), but it must not pass unnoticed. */}
                  {item.status === 'done' && item.warning && (
                    <Typography
                      variant="caption"
                      color="warning.main"
                      title={item.warning}
                      sx={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3 }}
                    >
                      {item.warning}
                    </Typography>
                  )}
                  {item.status === 'error' && item.error && (
                    <Typography
                      variant="caption"
                      color="error.main"
                      title={item.error}
                      sx={{
                        // Wrapped, but capped — backend tracebacks can be long and
                        // the panel is only 310px wide.
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: 1.3,
                      }}
                    >
                      {item.error}
                    </Typography>
                  )}
                </Box>

                {/* Offered only on rows whose producer said a re-run is safe and
                    meaningful — see `retry` in DocumentProgressContext. */}
                {item.status === 'error' && typeof item.retry === 'function' && (
                  <Button
                    size="small"
                    variant="text"
                    color="secondary"
                    startIcon={<ReplayIcon sx={{ fontSize: 14 }} />}
                    onClick={() => retryItem(item.id)}
                    sx={{ flexShrink: 0, minWidth: 0, px: 0.75, fontSize: '0.72rem', textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    Try again
                  </Button>
                )}
              </Box>
            );

            return isDone
              ? (
                <Link key={item.id} to={itemLink(item)} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                  {content}
                </Link>
              )
              : <React.Fragment key={item.id}>{content}</React.Fragment>;
          })}
        </Box>
      )}
    </Paper>
  );
};

export default DocumentProgressPanel;
